import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Container,
  InputAdornment,
  useTheme,
  Fade,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import axios from 'axios';
import {
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { gsap } from 'gsap';

interface PersonMatch {
  full_name: string;
  date_of_birth: string;
  raw_date_of_birth: string;
  age_at_course_end: number | null;
  email: string;
  phone: string;
  acuity_id: string;
  dob_needs_correction: boolean;
  appointment_count?: number;
  similarity_score?: number;
}

interface DOBResult {
  name: string;
  found: boolean;
  full_name: string;
  date_of_birth: string;
  raw_date_of_birth: string;
  age_at_course_end: number | null;
  email: string;
  phone: string;
  acuity_id: string;
  error?: string;
  dob_needs_correction: boolean;
  multiple_matches?: boolean;
  matches?: PersonMatch[];
}

interface VerificationResponse {
  results: DOBResult[];
  course_end_date: string;
  total_students: number;
  found_students: number;
}

const DOBVerifier: React.FC = () => {
  const [courseEndDate, setCourseEndDate] = useState<Date | null>(null);
  const [namesInput, setNamesInput] = useState('');
  const [results, setResults] = useState<DOBResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const theme = useTheme();
  
  // Editing state
  const [editingStudent, setEditingStudent] = useState<DOBResult | null>(null);
  const [editedDOB, setEditedDOB] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Multiple match selection state
  const [multipleMatchResult, setMultipleMatchResult] = useState<DOBResult | null>(null);
  const [showMultipleMatches, setShowMultipleMatches] = useState(false);
  
  // Animation refs
  const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Animate page elements on mount
    gsap.fromTo('.page-title', 
      { opacity: 0, y: -30 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
    );
    
    gsap.fromTo('.input-card', 
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: "power2.out" }
    );
  }, []);

  useEffect(() => {
    // Animate results table when results change
    if (results.length > 0) {
      const tableElement = document.querySelector('.results-table');
      if (tableElement) {
        gsap.fromTo(tableElement, 
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
        );
      }
    }
  }, [results]);

  const handleVerifyDOB = async () => {
    if (!courseEndDate) {
      setError('Please select a course end date');
      return;
    }

    if (!namesInput.trim()) {
      setError('Please enter at least one student name');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setResults([]);

    try {
      const names = namesInput
        .split('\n')
        .map(name => name.trim())
        .filter(name => name.length > 0);

      const response = await axios.post<VerificationResponse>('/api/dob-verifier/verify-dob', {
        names,
        course_end_date: format(courseEndDate, 'yyyy-MM-dd')
      });

      console.log('API Response:', response.data);
      console.log('Setting results to:', response.data.results);
      setResults(response.data.results);
      setSuccess(`Successfully processed ${response.data.total_students} students. Found ${response.data.found_students} in Acuity system.`);
      
    } catch (err: any) {
      console.error('API Error:', err);
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 422) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.response?.data?.error || err.message || 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearResults = () => {
    setResults([]);
    setError(null);
    setSuccess(null);
  };

  const handleClearInput = () => {
    setNamesInput('');
  };

  const exportToCSV = () => {
    if (results.length === 0) return;

    const csvHeaders = ['Name', 'Found', 'Full Name', 'Date of Birth', 'Raw DOB', 'Age at Course End', 'Email', 'Phone', 'Needs Correction', 'Error'];
    const csvData = results.map(result => [
      result.name,
      result.found ? 'Yes' : 'No',
      result.full_name,
      result.date_of_birth,
      result.raw_date_of_birth,
      result.age_at_course_end?.toString() || '',
      result.email,
      result.phone,
      result.dob_needs_correction ? 'Yes' : 'No',
      result.error || ''
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `dob_verification_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusChip = (result: DOBResult) => {
    if (result.multiple_matches) {
      return (
        <Chip 
          icon={<InfoIcon />} 
          label="Multiple Matches" 
          color="info" 
          size="small" 
        />
      );
    } else if (result.found && result.dob_needs_correction) {
      return (
        <Chip 
          icon={<InfoIcon />} 
          label="Needs Fix" 
          color="warning" 
          size="small" 
        />
      );
    } else if (result.found) {
      return (
        <Chip 
          icon={<CheckCircleIcon />} 
          label="Found" 
          color="success" 
          size="small" 
        />
      );
    } else {
      return (
        <Chip 
          icon={<ErrorIcon />} 
          label="Not Found" 
          color="error" 
          size="small" 
        />
      );
    }
  };

  const formatDateOfBirth = (dob: string) => {
    if (!dob) return '';
    try {
      const date = new Date(dob);
      return format(date, 'yyyy-MM-dd');
    } catch {
      return dob;
    }
  };

  const handleEditDOB = (student: DOBResult) => {
    setEditingStudent(student);
    setEditedDOB(null);
  };

  const handleSaveDOB = async () => {
    if (!editingStudent || !editedDOB) return;

    setSaving(true);
    try {
      const response = await axios.put('/api/dob-verifier/update-dob', {
        acuity_id: editingStudent.acuity_id,
        new_dob: format(editedDOB, 'yyyy-MM-dd')
      });

      if (response.data.success) {
        // Update the student in results
        const updatedResults = results.map(result => {
          if (result.acuity_id === editingStudent.acuity_id) {
            return {
              ...result,
              date_of_birth: format(editedDOB, 'yyyy-MM-dd'),
              raw_date_of_birth: response.data.acuity_format,
              dob_needs_correction: false,
              age_at_course_end: courseEndDate ? 
                calculateAge(format(editedDOB, 'yyyy-MM-dd'), format(courseEndDate, 'yyyy-MM-dd')) : null
            };
          }
          return result;
        });
        
        setResults(updatedResults);
        setSuccess(`DOB updated successfully for ${editingStudent.full_name}`);
        setEditingStudent(null);
        setEditedDOB(null);
      }
    } catch (err: any) {
      console.error('DOB Update Error:', err);
      setError(err.response?.data?.error || 'Failed to update DOB');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingStudent(null);
    setEditedDOB(null);
  };

  const handleShowMultipleMatches = (result: DOBResult) => {
    setMultipleMatchResult(result);
    setShowMultipleMatches(true);
  };

  const handleSelectMatch = (selectedMatch: PersonMatch) => {
    if (!multipleMatchResult) return;

    // Update the result with the selected match
    const updatedResults = results.map(result => {
      if (result.name === multipleMatchResult.name) {
        return {
          ...result,
          found: true,
          full_name: selectedMatch.full_name,
          date_of_birth: selectedMatch.date_of_birth,
          raw_date_of_birth: selectedMatch.raw_date_of_birth,
          age_at_course_end: selectedMatch.age_at_course_end,
          email: selectedMatch.email,
          phone: selectedMatch.phone,
          acuity_id: selectedMatch.acuity_id,
          dob_needs_correction: selectedMatch.dob_needs_correction,
          multiple_matches: false,
          matches: undefined,
          error: undefined
        };
      }
      return result;
    });

    setResults(updatedResults);
    setShowMultipleMatches(false);
    setMultipleMatchResult(null);
  };

  const handleCancelMultipleMatches = () => {
    setShowMultipleMatches(false);
    setMultipleMatchResult(null);
  };

  const calculateAge = (birthDate: string, endDate: string): number => {
    const birth = new Date(birthDate);
    const end = new Date(endDate);
    
    let age = end.getFullYear() - birth.getFullYear();
    const monthDiff = end.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && end.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const renderDOBCell = (result: DOBResult) => {
    if (result.multiple_matches) {
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleShowMultipleMatches(result)}
          sx={{ textTransform: 'none' }}
        >
          Select Person ({result.matches?.length || 0} found)
        </Button>
      );
    }
    
    if (result.dob_needs_correction && result.raw_date_of_birth) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`Raw value: ${result.raw_date_of_birth}`}>
            <Typography 
              variant="body2" 
              color="error.main"
              sx={{ 
                textDecoration: 'line-through',
                fontStyle: 'italic'
              }}
            >
              {result.raw_date_of_birth}
            </Typography>
          </Tooltip>
          <Tooltip title="Edit DOB">
            <IconButton 
              size="small" 
              color="primary"
              onClick={() => handleEditDOB(result)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    
    return formatDateOfBirth(result.date_of_birth) || '-';
  };



  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Title */}
        <Typography 
          variant="h4" 
          component="h1" 
          className="page-title"
          sx={{ 
            mb: 4, 
            fontWeight: 300, 
            color: theme.palette.primary.main,
            textAlign: 'center'
          }}
        >
          DOB Verifier
        </Typography>

        {/* Input Card */}
        <Card 
          className="input-card"
          elevation={3} 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.secondary.main}08)`
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* Course End Date */}
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Course End Date"
                  value={courseEndDate}
                  onChange={(newValue) => setCourseEndDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon color="primary" />
                          </InputAdornment>
                        ),
                      }
                    }
                  }}
                />
              </Grid>

              {/* Names Input */}
              <Grid item xs={12} md={8}>
                <TextField
                  label="Student Names (one per line)"
                  multiline
                  rows={4}
                  value={namesInput}
                  onChange={(e) => setNamesInput(e.target.value)}
                  fullWidth
                  required
                  placeholder="Enter student names, one per line..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Clear names">
                          <IconButton onClick={handleClearInput} size="small">
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleVerifyDOB}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                    sx={{ 
                      minWidth: 150,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem'
                    }}
                  >
                    {loading ? 'Verifying...' : 'Verify DOB'}
                  </Button>
                  
                  {results.length > 0 && (
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleClearResults}
                      startIcon={<RefreshIcon />}
                      sx={{ 
                        minWidth: 150,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1.1rem'
                      }}
                    >
                      Clear Results
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Success/Error Messages */}
        {error && (
          <Fade in>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </Fade>
        )}

        {success && (
          <Fade in>
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          </Fade>
        )}



        {/* Results Table */}
        {results.length > 0 ? (
          <Paper 
            className="results-table"
            elevation={3} 
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="h2">
                  DOB Verification Results ({results.length} students)
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={exportToCSV}
                  startIcon={<DownloadIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Export CSV
                </Button>
              </Box>
            </Box>
            
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Student Name</strong></TableCell>
                    <TableCell><strong>Full Name</strong></TableCell>
                    <TableCell><strong>Date of Birth</strong></TableCell>
                    <TableCell><strong>Age at Course End</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                        '&:hover': { backgroundColor: 'action.selected' }
                      }}
                    >
                      <TableCell>{getStatusChip(result)}</TableCell>
                      <TableCell>{result.name}</TableCell>
                      <TableCell>
                        {result.multiple_matches ? 
                          `Multiple people found` : 
                          (result.full_name || '-')
                        }
                      </TableCell>
                      <TableCell>{renderDOBCell(result)}</TableCell>
                      <TableCell>
                        {result.multiple_matches ? 
                          '-' : 
                          (result.age_at_course_end !== null ? 
                            `${result.age_at_course_end} years` : 
                            '-'
                          )
                        }
                      </TableCell>
                      <TableCell>
                        {result.multiple_matches ? 
                          '-' : 
                          (result.email || '-')
                        }
                      </TableCell>
                      <TableCell>
                        {result.multiple_matches ? 
                          '-' : 
                          (result.phone || '-')
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No results to display. Run a verification to see results here.
          </Typography>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Edit DOB Dialog */}
        <Dialog open={!!editingStudent} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit Date of Birth - {editingStudent?.full_name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current raw value: <strong>{editingStudent?.raw_date_of_birth}</strong>
              </Typography>
              <DatePicker
                label="Correct Date of Birth"
                value={editedDOB}
                onChange={(newValue) => setEditedDOB(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: "Enter the correct date of birth. This will update the value in Acuity."
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveDOB}
              variant="contained"
              disabled={!editedDOB || saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save to Acuity'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Multiple Matches Selection Dialog */}
        <Dialog open={showMultipleMatches} onClose={handleCancelMultipleMatches} maxWidth="md" fullWidth>
          <DialogTitle>
            Multiple People Found - {multipleMatchResult?.name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Found {multipleMatchResult?.matches?.length || 0} people with similar names. 
                Please select the correct person:
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Full Name</strong></TableCell>
                      <TableCell><strong>Date of Birth</strong></TableCell>
                      <TableCell><strong>Age at Course End</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Appointments</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {multipleMatchResult?.matches?.map((match, index) => (
                      <TableRow key={index}>
                        <TableCell>{match.full_name}</TableCell>
                        <TableCell>
                          {match.dob_needs_correction && match.raw_date_of_birth ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Tooltip title={`Raw value: ${match.raw_date_of_birth}`}>
                                <Typography 
                                  variant="body2" 
                                  color="error.main"
                                  sx={{ 
                                    textDecoration: 'line-through',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {match.raw_date_of_birth}
                                </Typography>
                              </Tooltip>
                              <Chip 
                                label="Needs Fix" 
                                color="warning" 
                                size="small"
                              />
                            </Box>
                          ) : (
                            formatDateOfBirth(match.date_of_birth) || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {match.age_at_course_end !== null ? 
                            `${match.age_at_course_end} years` : 
                            '-'
                          }
                        </TableCell>
                        <TableCell>{match.email || '-'}</TableCell>
                        <TableCell>{match.phone || '-'}</TableCell>
                        <TableCell>
                          {match.appointment_count ? 
                            `${match.appointment_count} appointments` : 
                            '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSelectMatch(match)}
                            sx={{ textTransform: 'none' }}
                          >
                            Select This Person
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCancelMultipleMatches}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default DOBVerifier; 